/**
 * ListActionsSettingsTab — Ustawienia → „Akcje" (własne akcje list tenanta, Faza 2).
 *
 * Ekran zaczyna się od WYBORU KARTOTEKI (każda kartoteka ma osobny zestaw akcji — decyzja
 * właścicielki 2026-09-10). Lista kartotek pochodzi z tego samego źródła co ekran
 * Ustawienia → Kartoteki: `GET /system-modules` (kartoteki systemowe) + `GET /modules`
 * (kartoteki własne LUB zainstalowane z huba). Podział jak w `ModulesOverviewTab`:
 *   - SYSTEMOWE = /system-modules ∪ /modules z `sourceTemplateModuleId != null` (z huba, od ITE),
 *   - WŁASNE    = /modules z `sourceTemplateModuleId == null` (utworzone przez tenanta).
 * Prezentacja: pełnej szerokości WIERSZE (lista), nie siatka kafelków.
 *
 * Po wejściu w kartotekę: „Akcje własne" (CRUD, SQL/webhook/powiązanie/dekret) + „Akcje
 * systemowe" (wbudowane z Fazy 1, tylko podgląd). Zaczynamy od kartoteki Kontrahenci.
 *
 * NIE mylić „kartoteki własnej" (custom module z Kreatora) z „modułem" jako pakietem sprzedażowym.
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  actionsForKartoteka,
  GENERIC_LIST_KARTOTEKA_KEY,
  GROUPS_DICTIONARY_BY_KARTOTEKA,
  isRecordCategoryKartotekaKey,
  kartotekaHasGroups,
  type ListActionDefinitionDTO,
  type ListActionKartotekaKey,
  type ListActionTypeConfig,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  ChevronLeft,
  ChevronRight,
  Database,
  LayoutGrid,
  List as ListIcon,
  ListChecks,
  PlayCircle,
  Plus,
  Search,
  Trash2,
  Wand2,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Checkbox } from '@/components/Checkbox';
import { listActionLucideIcon } from '@/components/configurable-table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DatePicker } from '@/components/DatePicker';
import { GroupsInput } from '@/components/groups/GroupsInput';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { IconPicker, renderIconByName } from '@/components/modules/IconPicker';
import { MultiSelect } from '@/components/MultiSelect';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Spinner } from '@/components/Spinner';
import { Tabs, type TabsItem } from '@/components/Tabs';
import type { SqlEditorHandle } from '@/components/ui/LazySqlEditor';
import { SqlField } from '@/components/ui/SqlField';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  isExcludedFromBulkSet,
  storedValueToDraft,
  valueControlKind,
} from '@/lib/list-actions/value-control';
import { localDateTimeInputToIso } from '@/lib/local-date';
import { buildSqlParamCompletions, LIST_ACTION_RESERVED_PARAMS } from '@/lib/sql-editor/params';
import { useTenantSqlSchema } from '@/lib/sql-editor/schema';

/**
 * Kartoteki-dokumenty współdzielą tabelę `documents`; slug (l. mn.) → klasa dokumentu.
 * `erp_documents` (Dokumenty z Symfonii) nie jest klasą dokumentu w domenie — brak wpisu.
 */
const DOC_SLUG_TO_CLASS: Record<string, string> = {
  sale_invoices: 'sale_invoice',
  purchase_invoices: 'purchase_invoice',
  contracts: 'contract',
  letters: 'letter',
  commercial: 'commercial',
  internal: 'internal',
  specjalne: 'specjalne',
};

/**
 * Kartoteki systemowe „podstawowe" — trzon CRM. Idą na górze zakładki „Systemowe",
 * nad dividerem; pozostałe systemowe (Dokumenty z Symfonii + moduły z huba/HR) → „Pozostałe".
 */
const BASIC_SYSTEM_SLUGS = new Set<string>([
  'contractors',
  'contractor_contacts',
  'companies',
  'employees',
  'users',
  'products',
  'warehouses',
  'tasks',
]);

interface SystemModuleListItem {
  slug: string;
  name: string;
  entityTableName: string;
  icon: string;
  color: string;
}
interface CustomModuleListItem {
  id: string;
  slug: string;
  name: string;
  icon: string | null;
  color: string | null;
  /**
   * Pochodzenie kartoteki: NIE-null = zainstalowana z szablonu huba (od ITE) → dla tenanta
   * jest SYSTEMOWA; null = utworzona przez tenanta → WŁASNA. Mirror `ModulesOverviewTab`.
   */
  sourceTemplateModuleId?: string | null;
}

/** Ujednolicony wiersz kartoteki na liście wyboru. */
interface KartotekaRowItem {
  key: string;
  name: string;
  icon: string;
  color: string;
  slug: string;
  kartotekaKey: ListActionKartotekaKey;
  /** Klasa dokumentu, gdy kartoteka jest dokumentem (chip „Dokumenty" + zawężenie KSeF/FK). */
  documentClass?: string;
  /** Czy kartoteka należy do obszaru Dokumenty (entity_table_name = 'documents'). */
  isDocument?: boolean;
  /** Kartoteka systemowa (ma endpoint pól /system-modules/:slug/fields). */
  isSystem: boolean;
  /** Liczba akcji wbudowanych (z katalogu Fazy 1) dostępnych dla tej kartoteki. */
  systemCount?: number;
  /** Liczba własnych akcji tenanta zdefiniowanych dla tej kartoteki. */
  customCount?: number;
}

/** Tryb prezentacji landingu wyboru kartoteki. */
type DisplayMode = 'list' | 'tiles';
const DISPLAY_MODE_STORAGE_KEY = 'listActions.settings.displayMode';

function readDisplayMode(): DisplayMode {
  try {
    const v = localStorage.getItem(DISPLAY_MODE_STORAGE_KEY);
    if (v === 'tiles' || v === 'list') return v;
  } catch {
    /* localStorage niedostępny (tryb prywatny / brak zgody) — użyj domyślnego */
  }
  return 'list';
}

/** Wybrana kartoteka do drill-in. `kartotekaKey` steruje katalogiem akcji wbudowanych. */
interface SelectedKartoteka {
  name: string;
  slug: string;
  kartotekaKey: ListActionKartotekaKey;
  documentClass?: string;
  /** Czy to kartoteka systemowa (ma endpoint pól /system-modules/:slug/fields). */
  isSystem: boolean;
}

/**
 * Mapowanie slugu kartoteki systemowej na klucz katalogu akcji. Slugi kartotek z osią
 * kategorii (kontrahenci/pracownicy/dokumenty…) są zarazem kluczami katalogu; kartoteki
 * własne i pozostałe → `generic` (tylko akcje uniwersalne, bez KSeF/FK).
 */
function slugToKartotekaKey(slug: string): ListActionKartotekaKey {
  return isRecordCategoryKartotekaKey(slug) ? slug : GENERIC_LIST_KARTOTEKA_KEY;
}

export function ListActionsSettingsTab() {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<SelectedKartoteka | null>(null);
  const [query, setQuery] = useState('');
  const [displayMode, setDisplayMode] = useState<DisplayMode>(() => readDisplayMode());

  // Zapamiętaj wybór trybu prezentacji (odporne na brak localStorage).
  useEffect(() => {
    try {
      localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, displayMode);
    } catch {
      /* localStorage niedostępny — pomijamy zapis */
    }
  }, [displayMode]);

  const systemQuery = useQuery({
    queryKey: ['system-modules'],
    queryFn: async () => {
      const res = await api.get<{ modules: SystemModuleListItem[] }>('/system-modules');
      return res.data.modules;
    },
  });
  const customQuery = useQuery({
    queryKey: ['modules'],
    queryFn: async () => {
      const res = await api.get<{ modules: CustomModuleListItem[] }>('/modules');
      return res.data.modules;
    },
  });

  // Wszystkie własne akcje tenanta (bez filtra kartoteki) — do policzenia „N własnych" per wiersz.
  const definitionsQuery = useQuery({
    queryKey: ['list-action-definitions'],
    queryFn: async () => {
      const res = await api.get<{ definitions: ListActionDefinitionDTO[] }>(
        '/list-action-definitions',
      );
      return res.data.definitions;
    },
  });

  const customData = useMemo(() => customQuery.data ?? [], [customQuery.data]);

  // Rozbicie liczby własnych akcji: `appliesTo === 'all'` liczy się do KAŻDEJ kartoteki,
  // pozostałe zliczamy per klucz kartoteki (mirror filtra endpointu GET).
  const customActionCounts = useMemo(() => {
    let all = 0;
    const perKey = new Map<string, number>();
    for (const def of definitionsQuery.data ?? []) {
      if (def.appliesTo === 'all') {
        all += 1;
      } else if (Array.isArray(def.appliesTo)) {
        for (const key of def.appliesTo) perKey.set(key, (perKey.get(key) ?? 0) + 1);
      }
    }
    return { all, perKey };
  }, [definitionsQuery.data]);

  const enrichCounts = useMemo(
    () =>
      (items: KartotekaRowItem[]): KartotekaRowItem[] =>
        items.map((it) => ({
          ...it,
          systemCount: actionsForKartoteka(it.kartotekaKey, it.documentClass).length,
          customCount:
            customActionCounts.all + (customActionCounts.perKey.get(it.kartotekaKey) ?? 0),
        })),
    [customActionCounts],
  );

  // SYSTEMOWE = kartoteki systemowe + zainstalowane z huba (sourceTemplateModuleId != null).
  const systemItems: KartotekaRowItem[] = useMemo(() => {
    const fromSystem = (systemQuery.data ?? []).map((m) => {
      const isDocument = m.entityTableName === 'documents';
      const documentClass = isDocument ? DOC_SLUG_TO_CLASS[m.slug] : undefined;
      return {
        key: `sys-${m.slug}`,
        name: m.name,
        icon: m.icon,
        color: m.color,
        slug: m.slug,
        kartotekaKey: (isDocument
          ? 'documents'
          : slugToKartotekaKey(m.slug)) as ListActionKartotekaKey,
        isDocument,
        documentClass,
        isSystem: true,
      };
    });
    const fromHub = customData
      .filter((m) => m.sourceTemplateModuleId != null)
      .map((m) => ({
        key: `hub-${m.id}`,
        name: m.name,
        icon: m.icon ?? 'Database',
        color: m.color ?? '#3B82F6',
        slug: m.slug,
        kartotekaKey: GENERIC_LIST_KARTOTEKA_KEY,
        isSystem: false,
      }));
    return [...fromSystem, ...fromHub];
  }, [systemQuery.data, customData]);

  // WŁASNE = utworzone przez tenanta (sourceTemplateModuleId == null).
  const customItems: KartotekaRowItem[] = useMemo(
    () =>
      customData
        .filter((m) => m.sourceTemplateModuleId == null)
        .map((m) => ({
          key: `own-${m.id}`,
          name: m.name,
          icon: m.icon ?? 'Database',
          color: m.color ?? '#3B82F6',
          slug: m.slug,
          kartotekaKey: GENERIC_LIST_KARTOTEKA_KEY,
          isSystem: false,
        })),
    [customData],
  );

  // Rozbicie kartotek systemowych na trzy grupy prezentacyjne:
  //  - Dokumenty  = kartoteki z obszaru Dokumenty (entity_table_name = 'documents'),
  //  - Podstawowe = trzon CRM (BASIC_SYSTEM_SLUGS),
  //  - Pozostałe  = reszta systemowych (Dokumenty z Symfonii + moduły z huba/HR).
  const documentItems = useMemo(() => systemItems.filter((it) => it.isDocument), [systemItems]);
  const basicItems = useMemo(
    () => systemItems.filter((it) => !it.isDocument && BASIC_SYSTEM_SLUGS.has(it.slug)),
    [systemItems],
  );
  const otherSystemItems = useMemo(
    () => systemItems.filter((it) => !it.isDocument && !BASIC_SYSTEM_SLUGS.has(it.slug)),
    [systemItems],
  );

  const q = query.trim().toLowerCase();
  const filterItems = (items: KartotekaRowItem[]) =>
    q ? items.filter((it) => it.name.toLowerCase().includes(q) || it.slug.includes(q)) : items;

  const docFiltered = enrichCounts(filterItems(documentItems));
  const basicFiltered = enrichCounts(filterItems(basicItems));
  const otherFiltered = enrichCounts(filterItems(otherSystemItems));
  const customFiltered = enrichCounts(filterItems(customItems));

  if (selected) {
    return (
      <ListActionsForKartoteka
        kartotekaKey={selected.kartotekaKey}
        documentClass={selected.documentClass}
        name={selected.name}
        slug={selected.slug}
        isSystem={selected.isSystem}
        onBack={() => setSelected(null)}
      />
    );
  }

  const selectKartoteka = (it: KartotekaRowItem) =>
    setSelected({
      name: it.name,
      slug: it.slug,
      kartotekaKey: it.kartotekaKey,
      documentClass: it.documentClass,
      isSystem: it.isSystem,
    });

  const isLoading = systemQuery.isLoading || customQuery.isLoading;
  const isError = systemQuery.isError || customQuery.isError;
  const searching = q !== '';
  const totalHits =
    docFiltered.length + basicFiltered.length + otherFiltered.length + customFiltered.length;

  // Zakładki (widoczne, gdy nie szukamy). Licznik = pełna liczba kartotek w grupie.
  const tabItems: TabsItem[] = [
    {
      value: 'docs',
      label: (
        <TabLabel
          text={t('tenant.listActions.settings.tabDocuments')}
          count={documentItems.length}
        />
      ),
      content: (
        <div className="space-y-4">
          <p className="text-xs text-text-muted">
            {t('tenant.listActions.settings.documentsHint')}
          </p>
          <GroupBody
            items={docFiltered}
            displayMode={displayMode}
            testIdPrefix="list-actions-kartoteka"
            onSelect={selectKartoteka}
            emptyText={t('tenant.listActions.settings.systemEmpty')}
          />
        </div>
      ),
    },
    {
      value: 'sys',
      label: (
        <TabLabel
          text={t('tenant.listActions.settings.tabSystem')}
          count={basicItems.length + otherSystemItems.length}
        />
      ),
      content: (
        <div className="space-y-6">
          <div>
            <GroupHeader
              label={t('tenant.listActions.settings.subBasic')}
              count={basicItems.length}
            />
            <GroupBody
              items={basicFiltered}
              displayMode={displayMode}
              testIdPrefix="list-actions-kartoteka"
              onSelect={selectKartoteka}
              emptyText={t('tenant.listActions.settings.systemEmpty')}
            />
          </div>
          <div className="border-t border-border-secondary pt-6">
            <GroupHeader
              label={t('tenant.listActions.settings.subOther')}
              count={otherSystemItems.length}
            />
            <GroupBody
              items={otherFiltered}
              displayMode={displayMode}
              testIdPrefix="list-actions-kartoteka"
              onSelect={selectKartoteka}
              emptyText={t('tenant.listActions.settings.systemEmpty')}
            />
          </div>
        </div>
      ),
    },
    {
      value: 'custom',
      label: (
        <TabLabel text={t('tenant.listActions.settings.tabCustom')} count={customItems.length} />
      ),
      content: (
        <div className="space-y-4">
          <p className="text-xs text-text-muted">{t('tenant.listActions.settings.customHint')}</p>
          <GroupBody
            items={customFiltered}
            displayMode={displayMode}
            testIdPrefix="list-actions-kartoteka-custom"
            onSelect={selectKartoteka}
            emptyText={t('tenant.listActions.settings.customKartotekiEmpty')}
          />
        </div>
      ),
    },
  ];

  return (
    <div data-testid="list-actions-settings" className="space-y-6 pb-8">
      <p className="text-sm text-text-secondary">
        {t('tenant.listActions.settings.pickKartoteka')}
      </p>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex-1">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('tenant.listActions.settings.searchAllHint')}
            aria-label={t('tenant.listActions.settings.searchAllHint')}
            prefixIcon={<Search className="h-4 w-4" aria-hidden="true" />}
            data-testid="list-actions-search"
          />
        </div>
        <DisplayModeToggle value={displayMode} onChange={setDisplayMode} />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : isError ? (
        <Card padding="md">
          <p className="text-error-500">
            {systemQuery.isError &&
            isAxiosError(systemQuery.error) &&
            systemQuery.error.response?.status === 403
              ? t('tenant.systemModules.forbidden')
              : t('tenant.systemModules.loadError')}
          </p>
        </Card>
      ) : searching ? (
        // Wyszukiwarka szuka po WSZYSTKICH grupach naraz — zamiast zakładek pokazujemy
        // wyniki pogrupowane (Dokumenty / Podstawowe / Pozostałe / Customowe).
        totalHits === 0 ? (
          <p className="text-sm text-text-muted" data-testid="list-actions-no-results">
            {t('tenant.listActions.settings.noResults', { phrase: query.trim() })}
          </p>
        ) : (
          <div className="space-y-6" data-testid="list-actions-search-results">
            <p className="text-xs text-text-muted">
              {t('tenant.listActions.settings.searchSummary', {
                count: totalHits,
                phrase: query.trim(),
              })}
            </p>
            <SearchGroup
              label={t('tenant.listActions.settings.tabDocuments')}
              items={docFiltered}
              displayMode={displayMode}
              testIdPrefix="list-actions-kartoteka"
              onSelect={selectKartoteka}
            />
            <SearchGroup
              label={t('tenant.listActions.settings.subBasic')}
              items={basicFiltered}
              displayMode={displayMode}
              testIdPrefix="list-actions-kartoteka"
              onSelect={selectKartoteka}
            />
            <SearchGroup
              label={t('tenant.listActions.settings.subOther')}
              items={otherFiltered}
              displayMode={displayMode}
              testIdPrefix="list-actions-kartoteka"
              onSelect={selectKartoteka}
            />
            <SearchGroup
              label={t('tenant.listActions.settings.tabCustom')}
              items={customFiltered}
              displayMode={displayMode}
              testIdPrefix="list-actions-kartoteka-custom"
              onSelect={selectKartoteka}
            />
          </div>
        )
      ) : (
        <Tabs items={tabItems} defaultValue="docs" idPrefix="list-actions-tabs" />
      )}
    </div>
  );
}

// ─── Znacznik wg design systemu (Figma „Badge · Subtle · Sm") ─────────────────
// Pigułka 20 px, Caption Medium 12/18, kolory z tokenów Workflow 3.0 (neutral / brand /
// warning). Lokalnie dla tego widoku — globalny `Badge` ma jeszcze starszy wygląd.

const DS_BADGE_TONE = {
  neutral: 'bg-[#f1f1f4] text-[#5c5c6b] dark:bg-gray-700 dark:text-gray-300',
  brand: 'bg-[#eef4ff] text-[#2455db] dark:bg-accent-900/30 dark:text-accent-300',
  warning: 'bg-[#fcedc8] text-[#a2700f] dark:bg-amber-900/30 dark:text-amber-300',
} as const;

function DsBadge({
  tone,
  children,
  'data-testid': testId,
}: {
  tone: keyof typeof DS_BADGE_TONE;
  children: ReactNode;
  'data-testid'?: string;
}) {
  return (
    <span
      className={`inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-full px-2 text-xs font-medium leading-[18px] ${DS_BADGE_TONE[tone]}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

// ─── Etykieta zakładki z licznikiem ───────────────────────────────────────────

function TabLabel({ text, count }: { text: string; count: number }) {
  return (
    <span className="flex items-center gap-2">
      {text}
      <DsBadge tone="neutral">{count}</DsBadge>
    </span>
  );
}

// ─── Nagłówek podgrupy (Podstawowe / Pozostałe) ───────────────────────────────

function GroupHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="mb-3 flex items-baseline gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">{label}</h3>
      <span className="text-xs text-text-muted">{count}</span>
    </div>
  );
}

// ─── Ciało grupy: kolekcja kartotek albo tekst „pusto" ────────────────────────

function GroupBody({
  items,
  displayMode,
  testIdPrefix,
  onSelect,
  emptyText,
}: {
  items: KartotekaRowItem[];
  displayMode: DisplayMode;
  testIdPrefix: string;
  onSelect: (item: KartotekaRowItem) => void;
  emptyText: string;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-text-muted">{emptyText}</p>;
  }
  return (
    <KartotekaCollection
      displayMode={displayMode}
      items={items}
      testIdPrefix={testIdPrefix}
      onSelect={onSelect}
    />
  );
}

// ─── Grupa wyników wyszukiwania (pomijana, gdy brak trafień) ───────────────────

function SearchGroup({
  label,
  items,
  displayMode,
  testIdPrefix,
  onSelect,
}: {
  label: string;
  items: KartotekaRowItem[];
  displayMode: DisplayMode;
  testIdPrefix: string;
  onSelect: (item: KartotekaRowItem) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <GroupHeader label={label} count={items.length} />
      <KartotekaCollection
        displayMode={displayMode}
        items={items}
        testIdPrefix={testIdPrefix}
        onSelect={onSelect}
      />
    </div>
  );
}

// ─── Przełącznik trybu prezentacji (Lista / Kafelki) ──────────────────────────

function DisplayModeToggle({
  value,
  onChange,
}: {
  value: DisplayMode;
  onChange: (mode: DisplayMode) => void;
}) {
  const { t } = useTranslation();
  const modes: { id: DisplayMode; label: string; icon: typeof ListIcon }[] = [
    { id: 'list', label: t('tenant.listActions.settings.displayList'), icon: ListIcon },
    { id: 'tiles', label: t('tenant.listActions.settings.displayTiles'), icon: LayoutGrid },
  ];
  return (
    // Figma „Segmented control" (zmiana widoku lista / kafelki): szary tor, aktywna pozycja
    // biała i uniesiona — wygląd z tej samej skórki co pasek chipów (`data-chip-bar`).
    <div
      className="flex items-center gap-1 self-start"
      role="group"
      aria-label={t('tenant.listActions.settings.displayModeLabel')}
      data-chip-bar="groups"
      data-testid="list-actions-display-mode"
    >
      {modes.map((m) => {
        const Icon = m.icon;
        const active = m.id === value;
        return (
          <Button
            key={m.id}
            variant="ghost"
            size="sm"
            aria-pressed={active}
            className={active ? 'font-medium text-text-primary' : 'text-text-secondary'}
            onClick={() => onChange(m.id)}
            data-testid={`list-actions-display-mode-${m.id}`}
            leftIcon={<Icon className="h-4 w-4" aria-hidden="true" />}
          >
            {m.label}
          </Button>
        );
      })}
    </div>
  );
}

// ─── Odznaki liczników akcji (systemowe / własne) na wierszu i kaflu ───────────

function KartotekaCountBadges({ item }: { item: KartotekaRowItem }) {
  const { t } = useTranslation();
  return (
    <>
      {item.systemCount ? (
        <DsBadge tone="neutral" data-testid={`list-actions-count-system-${item.slug}`}>
          {t('tenant.listActions.settings.systemCountBadge', { count: item.systemCount })}
        </DsBadge>
      ) : null}
      {item.customCount ? (
        <DsBadge tone="brand" data-testid={`list-actions-count-custom-${item.slug}`}>
          {t('tenant.listActions.settings.customCountBadge', { count: item.customCount })}
        </DsBadge>
      ) : null}
    </>
  );
}

// ─── Kolekcja kartotek: lista wierszy (2 kolumny na szerokich) albo kafelki ────

function KartotekaCollection({
  displayMode,
  items,
  onSelect,
  testIdPrefix,
}: {
  displayMode: DisplayMode;
  items: KartotekaRowItem[];
  onSelect: (item: KartotekaRowItem) => void;
  testIdPrefix: string;
}) {
  return displayMode === 'tiles' ? (
    <KartotekaTileGrid items={items} onSelect={onSelect} testIdPrefix={testIdPrefix} />
  ) : (
    <KartotekaRowList items={items} onSelect={onSelect} testIdPrefix={testIdPrefix} />
  );
}

// ─── Lista kartotek jako pełnej szerokości wiersze (2 kolumny na szerokich) ────

/**
 * Liczba kolumn wg RZECZYWISTEJ szerokości kontenera (nie viewportu) — dzięki temu
 * przy otwartym menu bocznym i pod-nawigacji Ustawień (wąski obszar treści) landing
 * schodzi do 1 kolumny nawet na szerokim monitorze. Próg ~800px kontenera.
 */
function useContainerColumns(minTwoColPx = 800) {
  const ref = useRef<HTMLDivElement>(null);
  const [twoCol, setTwoCol] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setTwoCol(e.contentRect.width >= minTwoColPx);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [minTwoColPx]);
  return { ref, twoCol };
}

function KartotekaRowList({
  items,
  onSelect,
  testIdPrefix,
}: {
  items: KartotekaRowItem[];
  onSelect: (item: KartotekaRowItem) => void;
  testIdPrefix: string;
}) {
  const { t } = useTranslation();
  const { ref, twoCol } = useContainerColumns();
  return (
    <div
      ref={ref}
      className="grid gap-2"
      style={{ gridTemplateColumns: twoCol ? 'repeat(2, minmax(0, 1fr))' : '1fr' }}
    >
      {items.map((it) => (
        <Button
          key={it.key}
          variant="ghost"
          onClick={() => onSelect(it)}
          data-testid={`${testIdPrefix}-${it.slug}`}
          className="w-full justify-start gap-2.5 rounded-lg border border-border-primary bg-bg-primary px-3 py-1.5 hover:border-accent-500 hover:bg-bg-primary"
          leftIcon={
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
              style={{ backgroundColor: `${it.color}20`, color: it.color }}
              aria-hidden="true"
            >
              {renderIconByName(it.icon, 'h-3.5 w-3.5')}
            </span>
          }
          rightIcon={
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-muted" aria-hidden="true" />
          }
        >
          <span className="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-text-primary">
            {it.name}
          </span>
          {it.isDocument ? (
            <DsBadge tone="warning" data-testid={`${testIdPrefix}-${it.slug}-doc-chip`}>
              {t('tenant.listActions.settings.documentChip')}
            </DsBadge>
          ) : null}
          <KartotekaCountBadges item={it} />
        </Button>
      ))}
    </div>
  );
}

// ─── Kartoteki jako kafelki (siatka auto-fit) ─────────────────────────────────

function KartotekaTileGrid({
  items,
  onSelect,
  testIdPrefix,
}: {
  items: KartotekaRowItem[];
  onSelect: (item: KartotekaRowItem) => void;
  testIdPrefix: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-3">
      {items.map((it) => (
        <Button
          key={it.key}
          variant="ghost"
          onClick={() => onSelect(it)}
          data-testid={`${testIdPrefix}-${it.slug}`}
          className="h-full flex-col items-stretch justify-start gap-3 rounded-lg border border-border-primary bg-bg-primary p-4 text-left hover:border-accent-500 hover:bg-bg-primary"
        >
          <div className="flex w-full items-center gap-2.5">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md"
              style={{ backgroundColor: `${it.color}20`, color: it.color }}
              aria-hidden="true"
            >
              {renderIconByName(it.icon, 'h-4 w-4')}
            </span>
            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">
              {it.name}
            </span>
            {it.isDocument ? (
              <DsBadge tone="warning" data-testid={`${testIdPrefix}-${it.slug}-doc-chip`}>
                {t('tenant.listActions.settings.documentChip')}
              </DsBadge>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <KartotekaCountBadges item={it} />
          </div>
        </Button>
      ))}
    </div>
  );
}

// ─── Drill-in: akcje jednej kartoteki (systemowe + własne) ────────────────────

function ListActionsForKartoteka({
  kartotekaKey,
  documentClass,
  name,
  slug,
  isSystem,
  onBack,
}: {
  kartotekaKey: ListActionKartotekaKey;
  documentClass?: string;
  name: string;
  slug: string;
  isSystem: boolean;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ListActionDefinitionDTO | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ListActionDefinitionDTO | null>(null);

  // Akcje systemowe (wbudowane) dla tej kartoteki — z katalogu w kodzie. Dla dokumentów klasa
  // zawęża akcje modułowe (KSeF/FK) do właściwej klasy dokumentu.
  const systemActions = useMemo(
    () => actionsForKartoteka(kartotekaKey, documentClass),
    [kartotekaKey, documentClass],
  );

  // Akcje WŁASNE tej kartoteki (z katalogu tenanta).
  const customKey = ['list-action-definitions', kartotekaKey];
  const customQuery = useQuery({
    queryKey: customKey,
    queryFn: async () => {
      const res = await api.get<{ definitions: ListActionDefinitionDTO[] }>(
        '/list-action-definitions',
        { params: { kartoteka: kartotekaKey } },
      );
      return res.data.definitions;
    },
  });
  const customDefs = customQuery.data ?? [];

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/list-action-definitions/${id}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.listActions.settings.deleted') });
      // Klucz bazowy: odświeża i detal (`[..., kartotekaKey]`), i licznik na landingu (`[...]`).
      void queryClient.invalidateQueries({ queryKey: ['list-action-definitions'] });
    },
    onError: () => toast.show({ variant: 'error', title: t('tenant.listActions.errorGeneric') }),
  });

  return (
    <div data-testid="list-actions-kartoteka-detail">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        data-testid="list-actions-back"
        className="mb-4 px-0"
        leftIcon={<ChevronLeft className="h-4 w-4" aria-hidden="true" />}
      >
        {t('tenant.listActions.settings.back')}
      </Button>

      <h2 className="mb-6 text-lg font-semibold text-text-primary">{name}</h2>

      {/* Akcje własne */}
      <section className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-text-muted">
            {t('tenant.listActions.settings.customActions')}
          </h3>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
            data-testid="list-actions-new"
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
          >
            {t('tenant.listActions.settings.newAction')}
          </Button>
        </div>
        {customQuery.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : customDefs.length === 0 ? (
          <p
            className="rounded-lg border border-dashed border-border-primary px-4 py-6 text-center text-sm text-text-muted"
            data-testid="list-actions-custom-empty"
          >
            {t('tenant.listActions.settings.customEmpty')}
          </p>
        ) : (
          <div className="flex flex-col gap-2" data-testid="list-actions-custom">
            {customDefs.map((def) => (
              <div
                key={def.id}
                className="flex items-center gap-1 rounded-lg border border-border-primary bg-bg-primary pr-2 transition-colors hover:border-accent-500"
                data-testid={`list-actions-custom-${def.id}`}
              >
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEditing(def);
                    setEditorOpen(true);
                  }}
                  className="flex min-w-0 flex-1 items-center justify-start gap-3 rounded-lg px-4 py-2.5"
                  data-testid={`list-actions-custom-${def.id}-edit`}
                >
                  <span className="shrink-0 text-text-muted" aria-hidden="true">
                    {renderIconByName(def.icon ?? DEFAULT_ACTION_ICON, 'h-4 w-4')}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-left text-[13px] font-medium text-text-primary">
                    {def.name}
                  </span>
                  <DsBadge tone={def.actionType === 'sql' ? 'warning' : 'neutral'}>
                    {def.actionType === 'sql'
                      ? t('tenant.listActions.editor.types.sql')
                      : t('tenant.listActions.editor.types.operation')}
                  </DsBadge>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-2 text-text-muted hover:text-error-600"
                  aria-label={t('common.delete')}
                  onClick={() => setPendingDelete(def)}
                  data-testid={`list-actions-custom-${def.id}-delete`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Akcje systemowe (wbudowane) */}
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">
          {t('tenant.listActions.settings.systemActions')}
        </h3>
        <p className="mb-3 text-xs text-text-muted">
          {t('tenant.listActions.settings.systemHint')}
        </p>
        {systemActions.length === 0 ? (
          <p className="text-sm text-text-muted">{t('tenant.listActions.settings.systemEmpty')}</p>
        ) : (
          <div className="flex flex-col gap-2" data-testid="list-actions-system">
            {systemActions.map((def) => {
              const SysIcon = listActionLucideIcon(def.icon);
              return (
                <div
                  key={def.id}
                  className="flex items-center gap-3 rounded-lg border border-border-primary bg-bg-primary px-4 py-2.5"
                  data-testid={`list-actions-system-${def.id}`}
                >
                  <SysIcon className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-[13px] font-medium text-text-primary">
                      {t(def.labelKey)}
                    </span>
                    <span className="text-xs text-text-muted">
                      {t(`tenant.listActions.settings.desc.${def.id}`)}
                    </span>
                  </span>
                  {def.moduleCode ? <DsBadge tone="neutral">{def.moduleCode}</DsBadge> : null}
                  <DsBadge tone={def.requiresSelection ? 'brand' : 'neutral'}>
                    {def.requiresSelection
                      ? t('tenant.listActions.settings.onSelection')
                      : t('tenant.listActions.settings.general')}
                  </DsBadge>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <ActionEditorModal
        open={editorOpen}
        editing={editing}
        kartotekaName={name}
        slug={slug}
        kartotekaKey={kartotekaKey}
        isSystem={isSystem}
        onClose={() => setEditorOpen(false)}
        onSaved={() => {
          setEditorOpen(false);
          // Klucz bazowy: odświeża i detal, i licznik akcji własnych na landingu.
          void queryClient.invalidateQueries({ queryKey: ['list-action-definitions'] });
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('tenant.listActions.settings.deleteTitle')}
        message={pendingDelete?.name ?? ''}
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

// ─── Edytor akcji własnej (duży modal) ────────────────────────────────────────

type EditorActionType = 'operation' | 'sql';
type OperationKind = 'set_field' | 'set_category' | 'add_group' | 'remove_group';

/** Domyślna ikona akcji własnej (gdy tenant nie wybierze innej). */
const DEFAULT_ACTION_ICON = 'Zap';

/** Parametry rezerwowe wstrzykiwane przez silnik — do wstawienia klikiem w edytorze SQL. */
const SQL_PARAMS = LIST_ACTION_RESERVED_PARAMS.map((name) => `:${name}`);

const EXAMPLE_SQL = `UPDATE contractors
   SET status = :new_status
 WHERE id = :record_id;`;

interface EditorFieldRow {
  id: string;
  name: string;
  slug: string;
  fieldType: string;
  /** Pole tylko-do-odczytu (np. z Symfonii) — nie do „Ustaw pole". */
  readOnly?: boolean;
}

/** Eksportowane wyłącznie dla testu okna (zabezpieczenie niezapisanych zmian) — poza tym lokalne. */
export function ActionEditorModal({
  open,
  kartotekaName,
  editing,
  slug,
  kartotekaKey,
  isSystem,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: ListActionDefinitionDTO | null;
  kartotekaName: string;
  slug: string;
  kartotekaKey: ListActionKartotekaKey;
  isSystem: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(DEFAULT_ACTION_ICON);
  const [type, setType] = useState<EditorActionType>('operation');
  const [operation, setOperation] = useState<OperationKind>('set_field');
  const [sql, setSql] = useState('');
  const [field, setField] = useState('');
  const [value, setValue] = useState('');
  const [opValue, setOpValue] = useState<string | null>(null);
  const [roleReqs, setRoleReqs] = useState<string[]>([]);
  const [requireConfirm, setRequireConfirm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const sqlEditorRef = useRef<SqlEditorHandle>(null);
  const sqlSchema = useTenantSqlSchema(open && type === 'sql');
  const sqlParams = useMemo(() => buildSqlParamCompletions(t, LIST_ACTION_RESERVED_PARAMS), [t]);

  // „Zmieniono" dla zabezpieczenia zamknięcia: typ akcji, operację, pole i wartość wybiera się
  // kliknięciem (bez zdarzeń wpisywania), więc porównujemy podpis stanu edytora z podpisem
  // zrobionym zaraz po prefillu przy otwarciu.
  const signature = JSON.stringify([
    name,
    icon,
    type,
    operation,
    sql,
    field,
    value,
    opValue,
    roleReqs,
    requireConfirm,
  ]);
  const [baselineSignature, setBaselineSignature] = useState<string | null>(null);
  const [prefillRev, setPrefillRev] = useState(0);
  useEffect(() => {
    // Efekt po rerenderze wywołanym prefillem (zmiana `prefillRev`) — `signature` jest już nowy.
    if (prefillRev > 0) setBaselineSignature(signature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillRev]);
  const isDirty = baselineSignature !== null && signature !== baselineSignature;

  // Przy otwarciu: prefill z edytowanej definicji albo reset dla nowej.
  useEffect(() => {
    if (!open) return;
    setPrefillRev((r) => r + 1);
    if (!editing) {
      resetForm();
      return;
    }
    setName(editing.name);
    setIcon(editing.icon ?? DEFAULT_ACTION_ICON);
    setRoleReqs(editing.allowedRoleIds ?? []);
    setRequireConfirm(editing.requireConfirm);
    setFormError(null);
    const cfg = editing.typeConfig;
    if (cfg.kind === 'sql') {
      setType('sql');
      setSql(cfg.sql);
      setOperation('set_field');
      setField('');
      setValue('');
      setOpValue(null);
    } else {
      setType('operation');
      setOperation(cfg.kind);
      setSql('');
      if (cfg.kind === 'set_field') {
        setField(cfg.field);
        setValue(cfg.value);
        setOpValue(null);
      } else if (cfg.kind === 'set_category') {
        setOpValue(cfg.categoryId);
        setField('');
        setValue('');
      } else {
        setOpValue(cfg.groupId);
        setField('');
        setValue('');
      }
    }
  }, [open, editing]);

  // Pola kartoteki do operacji „Ustaw pole" — tylko kartoteki systemowe mają ten endpoint.
  const fieldsQuery = useQuery({
    queryKey: ['system-module-fields', slug],
    queryFn: async () => {
      const res = await api.get<{ fields: EditorFieldRow[] }>(`/system-modules/${slug}/fields`);
      return res.data.fields;
    },
    enabled: open && type === 'operation' && operation === 'set_field' && isSystem && slug !== '',
  });
  // Referencje i pola tylko-do-odczytu nie nadają się do „Ustaw pole" (serwer też je odrzuca).
  const fieldOptions = (fieldsQuery.data ?? [])
    .filter((f) => !isExcludedFromBulkSet(f))
    .map((f) => ({ value: f.slug, label: f.name }));
  const selectedFieldType = (fieldsQuery.data ?? []).find((f) => f.slug === field)?.fieldType ?? '';
  const valueKind = valueControlKind(selectedFieldType);

  // Kategorie kartoteki do operacji „Zmień kategorię".
  const categoriesQuery = useQuery({
    queryKey: ['record-categories', kartotekaKey],
    queryFn: async () => {
      const res = await api.get<{ categories: { id: string; name: string }[] }>(
        '/record-categories',
        { params: { kartoteka: kartotekaKey } },
      );
      return res.data.categories;
    },
    enabled: open && type === 'operation' && operation === 'set_category',
  });
  const categoryOptions = (categoriesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  // Bramka „kto może wykonać" — uprawnienia nie są jeszcze rozbudowane, więc na razie
  // wybieramy spośród ról tenanta (4 systemowe). Docelowo dojdą realne uprawnienia.
  const rolesQuery = useQuery({
    queryKey: ['role-templates'],
    queryFn: async () => {
      const res = await api.get<{ roleTemplates: { id: string; name: string }[] }>(
        '/role-templates',
      );
      return res.data.roleTemplates;
    },
    enabled: open,
  });
  const roleOptions = (rolesQuery.data ?? []).map((r) => ({ value: r.id, label: r.name }));

  const types: { id: EditorActionType; label: string; icon: typeof Wand2 }[] = [
    { id: 'operation', label: t('tenant.listActions.editor.types.operation'), icon: Wand2 },
    { id: 'sql', label: t('tenant.listActions.editor.types.sql'), icon: Database },
  ];

  // Dokumenty (kartotekaKey === 'documents') nie mają osi kategorii ani grup — tylko „Ustaw pole".
  const isDocuments = kartotekaKey === 'documents';
  // Kod słownika grup kartoteki (pusty gdy kartoteka nie ma pola `groups`).
  const groupsDictionaryCode = kartotekaHasGroups(kartotekaKey)
    ? GROUPS_DICTIONARY_BY_KARTOTEKA[kartotekaKey as keyof typeof GROUPS_DICTIONARY_BY_KARTOTEKA]
    : '';
  const operations: { id: OperationKind; label: string }[] = isDocuments
    ? [{ id: 'set_field', label: t('tenant.listActions.editor.operations.setField') }]
    : [
        { id: 'set_field', label: t('tenant.listActions.editor.operations.setField') },
        { id: 'set_category', label: t('tenant.listActions.editor.operations.setCategory') },
        { id: 'add_group', label: t('tenant.listActions.editor.operations.addGroup') },
        { id: 'remove_group', label: t('tenant.listActions.editor.operations.removeGroup') },
      ];

  /** Składa `type_config` z aktualnego stanu edytora; null gdy braki (z komunikatem błędu). */
  function buildTypeConfig(): ListActionTypeConfig | null {
    if (type === 'sql') {
      if (!sql.trim()) return null;
      return { kind: 'sql', sql: sql.trim(), mode: 'bulk_set' };
    }
    if (operation === 'set_field') {
      if (!field || value === '') return null;
      // Pole wykluczone z „Ustaw pole" (referencja / tylko do odczytu — np. akcja zapisana przed
      // tą regułą): serwer by je odrzucił, więc blokujemy zapis jak niekompletny.
      const selected = (fieldsQuery.data ?? []).find((f) => f.slug === field);
      if (selected !== undefined && isExcludedFromBulkSet(selected)) return null;
      // Data z godziną: zapisana wartość musi być ISO ze strefą (akcje sprzed tej reguły mogą
      // trzymać datę bez strefy — wtedy wymagamy ponownego wyboru, jak przy pustym polu).
      if (valueKind === 'datetime' && storedValueToDraft('datetime', value) === '') return null;
      return { kind: 'set_field', field, value };
    }
    if (operation === 'set_category') {
      if (!opValue) return null;
      return { kind: 'set_category', categoryId: opValue };
    }
    if (operation === 'add_group' || operation === 'remove_group') {
      if (!opValue) return null;
      return { kind: operation, groupId: opValue };
    }
    return null;
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const typeConfig = buildTypeConfig();
      if (!name.trim() || !typeConfig) {
        throw new Error('INCOMPLETE');
      }
      const body = {
        name: name.trim(),
        icon,
        typeConfig,
        appliesTo: [kartotekaKey],
        requireConfirm,
        allowedRoleIds: roleReqs,
      };
      if (editing) {
        await api.patch(`/list-action-definitions/${editing.id}`, body);
      } else {
        await api.post('/list-action-definitions', body);
      }
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.listActions.editor.saved') });
      resetForm();
      onSaved();
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'INCOMPLETE') {
        setFormError(t('tenant.listActions.editor.incomplete'));
        return;
      }
      if (isAxiosError(err) && err.response?.status === 409) {
        setFormError(t('tenant.listActions.editor.nameTaken'));
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.listActions.errorGeneric') });
    },
  });

  function resetForm(): void {
    setName('');
    setIcon(DEFAULT_ACTION_ICON);
    setType('operation');
    setOperation('set_field');
    setSql('');
    setField('');
    setValue('');
    setOpValue(null);
    setRoleReqs([]);
    setRequireConfirm(false);
    setFormError(null);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="2xl"
      fixedHeight
      guardUnsaved
      dirty={isDirty}
      // W trakcie zapisu nie da się zamknąć okna tłem ani Esc (jak „Anuluj").
      closeOnBackdrop={!saveMutation.isPending}
      closeOnEsc={!saveMutation.isPending}
      title={`${editing ? t('tenant.listActions.editor.editTitle') : t('tenant.listActions.editor.title')} · ${kartotekaName}`}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <Button
            variant="ghost"
            leftIcon={<PlayCircle className="h-4 w-4" aria-hidden="true" />}
            data-testid="list-actions-editor-test"
          >
            {t('tenant.listActions.editor.test')}
          </Button>
          <div className="flex items-center gap-3">
            {formError && (
              <span className="text-xs text-text-danger" data-testid="list-actions-editor-error">
                {formError}
              </span>
            )}
            <Button
              variant="secondary"
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              loading={saveMutation.isPending}
              onClick={() => {
                setFormError(null);
                saveMutation.mutate();
              }}
              data-testid="list-actions-editor-save"
            >
              {t('tenant.listActions.editor.save')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-5" data-testid="list-actions-editor">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Input
              label={t('tenant.listActions.editor.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="list-actions-editor-name"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-secondary">
              {t('tenant.listActions.editor.icon')}
            </label>
            <IconPicker
              value={icon}
              onChange={setIcon}
              ariaLabel={t('tenant.listActions.editor.icon')}
              triggerClassName="!py-1"
            />
          </div>
        </div>

        {/* Typ akcji */}
        <div>
          <span className="text-sm font-medium text-text-secondary">
            {t('tenant.listActions.editor.type')}
          </span>
          <div className="mt-2 flex flex-wrap gap-2">
            {types.map((ty) => {
              const Icon = ty.icon;
              const active = ty.id === type;
              return (
                <Button
                  key={ty.id}
                  variant={active ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setType(ty.id)}
                  data-testid={`list-actions-editor-type-${ty.id}`}
                  leftIcon={<Icon className="h-4 w-4" aria-hidden="true" />}
                >
                  {ty.label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Operacje — dropdown operacji + formularz per operacja */}
        {type === 'operation' && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-text-secondary">
                {t('tenant.listActions.editor.operation')}
              </label>
              <SearchableSelect
                options={operations.map((o) => ({ value: o.id, label: o.label }))}
                value={operation}
                onChange={(v) => {
                  setOperation((v as OperationKind) ?? 'set_field');
                  setOpValue(null);
                }}
                data-testid="list-actions-editor-operation"
              />
            </div>

            {operation === 'set_field' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-text-secondary">
                    {t('tenant.listActions.editor.field')}
                  </label>
                  <SearchableSelect
                    options={fieldOptions}
                    value={field || null}
                    onChange={(v) => {
                      setField(v ?? '');
                      setValue(''); // typ pola się zmienia → reset wartości
                    }}
                    placeholder={t('tenant.listActions.editor.fieldPlaceholder')}
                    allowClear
                    disabled={!isSystem}
                    data-testid="list-actions-editor-field"
                  />
                  {!isSystem && (
                    <span className="text-xs text-text-muted">
                      {t('tenant.listActions.editor.fieldCustomHint')}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-text-secondary">
                    {t('tenant.listActions.editor.value')}
                  </label>
                  {valueKind === 'bool' ? (
                    <Checkbox
                      label={t('tenant.listActions.editor.valueBool')}
                      checked={value === 'true'}
                      onChange={(e) => setValue(e.target.checked ? 'true' : 'false')}
                      disabled={!field}
                    />
                  ) : valueKind === 'date' ? (
                    <DatePicker
                      value={value}
                      onChange={setValue}
                      disabled={!field}
                      aria-label={t('tenant.listActions.editor.value')}
                      data-testid="list-actions-editor-value"
                    />
                  ) : valueKind === 'datetime' ? (
                    // Stan trzyma ISO ze strefą (to idzie do API); input pokazuje czas lokalny.
                    <Input
                      type="datetime-local"
                      value={storedValueToDraft('datetime', value)}
                      onChange={(e) => setValue(localDateTimeInputToIso(e.target.value) ?? '')}
                      disabled={!field}
                      aria-label={t('tenant.listActions.editor.value')}
                      data-testid="list-actions-editor-value"
                    />
                  ) : (
                    <>
                      <Input
                        type={valueKind === 'number' ? 'number' : 'text'}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        disabled={!field}
                        data-testid="list-actions-editor-value"
                      />
                      {valueKind === 'select' && (
                        <span className="text-xs text-text-muted">
                          {t('tenant.listActions.editor.valueSelectHint')}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {operation === 'set_category' && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-text-secondary">
                  {t('tenant.listActions.editor.operations.setCategory')}
                </label>
                <SearchableSelect
                  options={categoryOptions}
                  value={opValue}
                  onChange={setOpValue}
                  placeholder={t('tenant.listActions.editor.categoryPlaceholder')}
                  allowClear
                />
              </div>
            )}

            {(operation === 'add_group' || operation === 'remove_group') && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-text-secondary">
                  {t('tenant.listActions.editor.group')}
                </label>
                {groupsDictionaryCode ? (
                  <GroupsInput
                    dictionaryCode={groupsDictionaryCode}
                    value={opValue ? [opValue] : []}
                    onChange={(codes) => setOpValue(codes[codes.length - 1] ?? null)}
                    data-testid="list-actions-editor-group"
                  />
                ) : (
                  <span className="text-xs text-text-muted">
                    {t('tenant.listActions.editor.groupUnavailable')}
                  </span>
                )}
                <span className="text-xs text-text-muted">
                  {t('tenant.listActions.editor.groupHint')}
                </span>
              </div>
            )}
          </div>
        )}

        {type === 'sql' && (
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-text-secondary">
                {t('tenant.listActions.editor.sql')}
              </span>
              <span className="text-xs text-text-muted">
                {t('tenant.listActions.editor.paramsHint')}
              </span>
            </div>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {SQL_PARAMS.map((p) => (
                <Button
                  key={p}
                  variant="secondary"
                  size="sm"
                  className="font-mono"
                  onClick={() => {
                    if (sqlEditorRef.current) sqlEditorRef.current.insertAtCursor(p);
                    else setSql((prev) => `${prev}${p}`);
                  }}
                  data-testid={`list-actions-editor-param-${p.slice(1)}`}
                >
                  {p}
                </Button>
              ))}
            </div>
            <SqlField
              ref={sqlEditorRef}
              value={sql}
              onChange={setSql}
              schema={sqlSchema}
              parameters={sqlParams}
              placeholder={EXAMPLE_SQL}
              aria-label={t('tenant.listActions.editor.sql')}
              data-testid="list-actions-editor-sql"
            />
            <p className="mt-2 text-xs text-text-secondary">
              {t('tenant.listActions.editor.sqlSafety')}
            </p>
          </div>
        )}

        {/* Bramka: które role mogą wykonać (na razie rola zamiast uprawnień) */}
        <div className="flex flex-col gap-1.5">
          <MultiSelect
            label={t('tenant.listActions.editor.role')}
            options={roleOptions}
            value={roleReqs}
            onChange={setRoleReqs}
            placeholder={t('tenant.listActions.editor.roleNone')}
            data-testid="list-actions-editor-permission"
          />
          <span className="text-xs text-text-muted">{t('tenant.listActions.editor.roleHint')}</span>
        </div>

        <div className="flex items-start gap-2 rounded-lg bg-bg-tertiary px-3 py-2 text-xs text-text-muted">
          <ListChecks className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('tenant.listActions.editor.runsOnSelected')}</span>
        </div>

        <div>
          <Checkbox
            label={t('tenant.listActions.editor.confirm')}
            checked={requireConfirm}
            onChange={(e) => setRequireConfirm(e.target.checked)}
          />
          <p className="mt-1 pl-6 text-xs text-text-muted">
            {t('tenant.listActions.editor.confirmHint')}
          </p>
        </div>
      </div>
    </Modal>
  );
}
